import quoteServices from '../../../../../../services/oneButtonServices/quoteServices';
import opportunityInfoServices from '../../../../../../services/oneButtonServices/opportunityInfoService';
import {
    getContractApiErrorMessage,
    getContractApiResultErrorMessage,
} from '../../../../../../helpers/contractApiErrorMessage';
import { financePartnerNormLower } from './financePartnerNormalize';

export type FinancePartnerSyncStatusRow = {
    ProductType?: string;
    productType?: string;
    IsLeadCreate?: boolean;
    IsContactSync?: boolean;
    IsUtility?: boolean;
    IsLeadSystem?: boolean;
    IsTilte?: boolean;
    IsTitle?: boolean;
    IsCreditPass?: boolean;
};

export type FinancePartnerSyncFlag = 'lead' | 'contact' | 'utility' | 'leadSystem' | 'title';

const MAX_SYNC_RETRIES = 5;
const RETRY_DELAY_MS = 1000;

export function isQuoteApiResponseSuccess(res: unknown): boolean {
    if (res === true || res === 'true') return true;
    if (!res || typeof res !== 'object') return false;
    const row = res as Record<string, unknown>;
    return (
        row.IsSuccessful === true ||
        row.isSuccesful === true ||
        row.isSuccessful === true ||
        row.IsSuccessful === 'true' ||
        row.isSuccesfull === true ||
        row.isSuccessfull === true ||
        String(row.Message ?? '')
            .toLowerCase()
            .includes('success')
    );
}

export function partnerNeedsUtilityAndLeadSystem(partnerName: string): boolean {
    const key = financePartnerNormLower(partnerName);
    return key === 'igs' || key.includes('lightreach');
}

export function partnerIsCash(partnerName: string): boolean {
    return financePartnerNormLower(partnerName).includes('cash');
}

export function financePartnerProductTypeLabel(product: string): string {
    if (product === 'Roof' || product.toUpperCase() === 'ROOF ONLY') return 'Roof Only';
    if (product === 'Battery') return 'Battery Only';
    return product;
}

export function financePartnerProductCode(product: string): string {
    if (product === 'Solar') return 'S';
    if (product === 'Roof') return 'R';
    if (product === 'Battery') return 'B';
    const lower = product.toLowerCase();
    if (lower.includes('solar')) return 'S';
    if (lower.includes('roof')) return 'R';
    if (lower.includes('battery')) return 'B';
    return 'S';
}

export function matchFinancePartnerSyncRow(
    syncList: FinancePartnerSyncStatusRow[],
    productType: string,
): FinancePartnerSyncStatusRow | undefined {
    const normalized = productType.replace('Only', '').trim();
    return syncList.find(
        (row) =>
            row.ProductType === productType ||
            row.productType === productType ||
            row.ProductType === normalized ||
            row.productType === normalized ||
            (!normalized && row),
    );
}

export function isFinancePartnerSyncFlagDone(
    syncList: FinancePartnerSyncStatusRow[],
    productType: string,
    flag: FinancePartnerSyncFlag,
): boolean {
    const match = matchFinancePartnerSyncRow(syncList, productType);
    if (!match) return false;
    switch (flag) {
        case 'lead':
            return match.IsLeadCreate === true;
        case 'contact':
            return match.IsContactSync === true;
        case 'utility':
            return match.IsUtility === true;
        case 'leadSystem':
            return match.IsLeadSystem === true;
        case 'title':
            return match.IsTilte === true || match.IsTitle === true;
        default:
            return false;
    }
}

export async function loadFinancePartnerSyncStatuses(
    opportunityId: string,
): Promise<FinancePartnerSyncStatusRow[]> {
    const res = await quoteServices.getSyncStatusList(opportunityId);
    const list = res?.FinancePartnerSyncStatuses ?? (Array.isArray(res) ? res : []);
    return Array.isArray(list) ? list : [];
}

export function isRoofOnlyProductType(productType: string): boolean {
    const upper = productType.toUpperCase();
    return upper === 'RO' || upper === 'ROOF ONLY' || upper === 'ROOF' || productType === 'Roof';
}

/** Ensure lead exists and contact is synced for one product type. */
export async function ensureLeadAndContactForProduct(args: {
    opportunityId: string;
    productType: string;
    productCode?: string;
    createLeadIfMissing?: boolean;
    contactSyncExisting?: boolean;
}): Promise<void> {
    const productType = financePartnerProductTypeLabel(args.productType);
    const productCode = args.productCode ?? financePartnerProductCode(args.productType);

    const leadCheck = await quoteServices.isLeadCreated(args.opportunityId, productType);
    if (args.createLeadIfMissing !== false && !leadCheck?.IsSuccessful) {
        const createRes = await quoteServices.createLead({
            opportunityId: args.opportunityId,
            productSelection: {
                Product_Category: productCode,
                LeadForProduct: productCode,
            },
        });
        if (createRes === '' || createRes?.IsSuccessful === false) {
            throw new Error(
                getContractApiResultErrorMessage(createRes, `Lead creation failed for ${productType}`),
            );
        }
    }

    await quoteServices.isContactCreated(args.opportunityId, productType);
    await quoteServices.createContactFinancePartner(
        args.opportunityId,
        productType,
        true,
        args.contactSyncExisting ?? false,
    );
}

export async function syncUtilityWithRetries(args: {
    opportunityId: string;
    productType: string;
    partnerName?: string;
    maxRetries?: number;
}): Promise<void> {
    const productType = financePartnerProductTypeLabel(args.productType);
    const isRoofOnly = isRoofOnlyProductType(productType);
    const maxRetries = args.maxRetries ?? MAX_SYNC_RETRIES;
    let lastUtilityResponse: unknown = null;

    for (let attempt = 0; attempt < maxRetries; attempt += 1) {
        try {
            const utilRes = await quoteServices.updateUtilitiesSunnova(
                args.opportunityId,
                isRoofOnly,
                productType,
            );
            if (isQuoteApiResponseSuccess(utilRes)) return;
            lastUtilityResponse = utilRes;
        } catch (error) {
            lastUtilityResponse = error;
        }
        if (attempt + 1 < maxRetries) {
            await new Promise((resolve) => window.setTimeout(resolve, RETRY_DELAY_MS));
        }
    }

    const partnerLabel = args.partnerName?.trim() || 'the finance partner';
    const utilityFallback = `There was an error syncing Utility Information to ${partnerLabel}, please ensure the correct Utility Company and Installation Address/ Zip code has been selected, ensure that the 'Current All In Utility Rate' and 'Annual Usage' values have been entered.`;
    const message =
        getContractApiResultErrorMessage(lastUtilityResponse, '') ||
        getContractApiErrorMessage(lastUtilityResponse, utilityFallback);
    throw new Error(message);
}

export async function syncLeadSystemWithRetries(args: {
    opportunityId: string;
    productType: string;
    productCategoryOverride?: string;
    maxRetries?: number;
    onSuccess?: () => Promise<void>;
}): Promise<void> {
    const productType = financePartnerProductTypeLabel(args.productType);
    const productCode = financePartnerProductCode(args.productType);
    const maxRetries = args.maxRetries ?? MAX_SYNC_RETRIES;
    let lastLeadSystemResponse: unknown = null;

    for (let attempt = 0; attempt < maxRetries; attempt += 1) {
        try {
            const payload = {
                productSelection: {
                    contractType: '',
                    Product_Category: args.productCategoryOverride ?? productCode,
                    LeadForProduct: productCode,
                },
                opportunityId: args.opportunityId,
            };
            const sysRes = await quoteServices.createLeadSystem(payload);
            if (isQuoteApiResponseSuccess(sysRes)) {
                if (args.onSuccess) await args.onSuccess();
                await opportunityInfoServices.displayContacts(args.opportunityId);
                return;
            }
            lastLeadSystemResponse = sysRes;
        } catch (error) {
            lastLeadSystemResponse = error;
        }
        if (attempt + 1 < maxRetries) {
            await new Promise((resolve) => window.setTimeout(resolve, RETRY_DELAY_MS));
        }
    }

    const message =
        getContractApiResultErrorMessage(lastLeadSystemResponse, '') ||
        getContractApiErrorMessage(
            lastLeadSystemResponse,
            'Failed to create the Lead System in the finance partner portal after multiple retries. Please verify the system design is complete and try again.',
        );
    throw new Error(message);
}
